import { Injectable, OnDestroy } from '@angular/core';
import { HighchartsThemeService, PagedRecords } from '@frk/eds-components';
import distributionHistoricalData from '@graphql/pricing-distribution/distribution-history-international.graphql';
import distributionHistoricalDataUS from '@graphql/pricing-distribution/distribution-history-us.graphql';
import distributionLatestData from '@graphql/pricing-distribution/distribution-international.graphql';
import distributionLatestDataUS from '@graphql/pricing-distribution/distribution-us.graphql';
import pricingHistoricalData from '@graphql/pricing-distribution/pricing-historical.graphql';
import { PricingHistory, Product, ShareClass } from '@models';
import { DistributionHistory } from '@products/models/distribution-history';
import { DistributionHistoryService } from '@products/services/distribution-history.service';
import { FundOverviewService } from '@products/services/fund-overview.service';
import { ProductTypeParameter } from '@products/services/graphql-fund-data.service';
import { hasValue } from '@products/utils/mappers/mapper.utils';
import { DebugService } from '@services/debug.service';
import { SiteConfigService } from '@services/site-config.service';
import { TranslateService } from '@shared/translate/translate.service';
import {
  ConfigurationId,
  DistributionNavState,
  DistributionState,
  DownloadableTableRowData,
  ProductDetailConfiguration,
  ProductType,
} from '@types';
import { symbols } from '@utils/il8n/currency';
import { Logger } from '@utils/logger';
import { getHighchartsLocaleFormat } from '@utils/text/date-utils';
import { parseNumber } from '@utils/text/number-utils';
import { SeriesOptionsType } from 'highcharts';
import cloneDeep from 'lodash/cloneDeep';
import first from 'lodash/first';
import sortedUniq from 'lodash/sortedUniq';
import dayjs from 'dayjs';
import { forkJoin, Observable, Subject } from 'rxjs';
import { DISTRIBUTION_NAV_INITIAL_STATE } from './pricing-distribution.config';
import {
  ExcelDownloadService,
  FtCellObject,
  getCellObject,
} from '@products/services/excel-download.service';
import { PricingHistoryService } from '@products/services/pricing-history.service';
import { takeUntil } from 'rxjs/operators';

const logger = Logger.getLogger('DistributionChartService');

@Injectable({
  providedIn: 'root',
})
export class DistributionChartService implements OnDestroy {
  private state: DistributionNavState;
  private configurationName: ConfigurationId;
  public distributionState: Subject<DistributionState>;
  protected pagedRecords: PagedRecords<DistributionHistory>;
  private unsubscribe$: Subject<void> = new Subject<void>();
  /**
   * default pagesize for the tables
   */
  pageSize = 10;

  constructor(
    private distributionHistoryService: DistributionHistoryService,
    private pricingHistoryService: PricingHistoryService,
    private siteConfigService: SiteConfigService,
    private fundOverviewService: FundOverviewService,
    private translateService: TranslateService,
    private excelDownloadService: ExcelDownloadService,
    private debugService: DebugService,
    private highchartsTheme: HighchartsThemeService
  ) {
    this.pagedRecords = new PagedRecords<DistributionHistory>(this.pageSize); // Page size set to '10', ref. by prod site.
    this.distributionState = new Subject();
  }

  /**
   * Call register method of fund overview & distribution history service.
   * @param productDetailConfiguration the configuration received on page detail load
   */
  public populate(
    productDetailConfiguration: ProductDetailConfiguration
  ): void {
    this.state = cloneDeep(DISTRIBUTION_NAV_INITIAL_STATE);

    const requestArray = [
      this.fundOverviewService.register(
        this.siteConfigService.isSiteInternational()
          ? distributionLatestData
          : distributionLatestDataUS,
        {
          configurationName: this.getConfigurationName(),
          productType: ProductTypeParameter.MUTUAL_FUNDS,
          fundId: productDetailConfiguration.fundId,
          shareClassCode: productDetailConfiguration.shareClassCode,
        }
      ),
      this.distributionHistoryService.register(
        this.siteConfigService.isSiteInternational()
          ? distributionHistoricalData
          : distributionHistoricalDataUS,
        {
          configurationName: this.getConfigurationName(),
          productType: ProductTypeParameter.MUTUAL_FUNDS,
          fundId: productDetailConfiguration.fundId,
          shareClassCode: productDetailConfiguration.shareClassCode,
        }
      ),
    ];

    // Distribution Chart with NAV

    if (
      productDetailConfiguration.componentType === 'Distribution Chart with NAV'
    ) {
      requestArray.push(
        this.pricingHistoryService.register(pricingHistoricalData, {
          configurationName: this.getConfigurationName(),
          productType: ProductTypeParameter.MUTUAL_FUNDS,
          fundId: productDetailConfiguration.fundId,
          shareClassCode: productDetailConfiguration.shareClassCode,
        })
      );
    }

    // Subscribing Addtional nav data from pricing history as per distribution Chart with NAV component.
    forkJoin(requestArray).subscribe(
      (mergedData: [Product, DistributionHistory[], PricingHistory[]]) => {
        this.mapState(mergedData);
      }
    );
  }

  /**
   * Split the merged details and map the overview & distribution data to the state accordingly.
   * Map the decorated config and any other setting to the state.
   * @param mergedData merged response for product & distribution histories.
   */
  public mapState(
    mergedData: [Product, DistributionHistory[], PricingHistory[]]
  ): void {
    const [overviewData, distributionHistories, pricingHistories] = mergedData;

    this.state.fundName = overviewData.fundName;
    this.state.productType = overviewData.productType;

    this.state.siteConfig = this.siteConfigService.product?.site;
    this.state.isSiteIntl = this.siteConfigService.isSiteInternational();

    const shareClassDetail: ShareClass = overviewData.shareClasses[0];

    // *** US specific state assignments. ***
    if (!this.state.isSiteIntl) {
      // Map Distribution Schedules.
      this.state.schedules = {
        distributionIndicator: shareClassDetail.distributionIndicator,
        dividendFrequency: shareClassDetail.dividendFrequency,
        capgainFrequency: hasValue(shareClassDetail, 'capgainFrequency')
          ? shareClassDetail.capgainFrequency
          : null,
      };

      // Map SalesChargeBreakPoint.
      this.state.salesChargeBreakPoints =
        shareClassDetail.salesChargeBreakPoints;

      // Map Yields.
      this.state.yields = shareClassDetail.yields;
    }

    this.state.latestConfig.isSgHk = this.siteConfigService.isSgHk();
    this.state.latestConfig.showCanadaDistForEtfMfMmf = this.showCanadaDistForEtfMfMmf(
      this.state.productType as ProductType
    );
    this.state.latestConfig.isSingleShareClass =
      shareClassDetail.isSingleShareClass;
    this.state.latestConfig.shareClassName = shareClassDetail.shareClassName;
    this.state.latestConfig.shareClassInceptionDate =
      shareClassDetail.inceptionDate;
    this.state.latestConfig.shareClassInceptionDateStd =
      shareClassDetail.inceptionDateStd;
    this.state.latestConfig.currencyCode = shareClassDetail.shareClassCurrency;
    this.state.latestConfig.hideExDistDate = this.hideExDistDateForCountry();

    if (shareClassDetail?.distribution.length) {
      if (!this.state.isSiteIntl) {
        this.state.latestConfig.navDate = shareClassDetail?.nav?.navAsOfDate;
      }
    }

    if (distributionHistories.length) {
      this.state.historicalData = distributionHistories.filter(
        (distributionHistory: DistributionHistory) => {
          return (
            dayjs().valueOf() >
            dayjs(
              distributionHistory.distributionAsOfDateStd,
              'YYYY-MM-DD'
            ).valueOf()
          );
        }
      );

      if (pricingHistories?.length) {
        this.state.pricingHistoricalData = pricingHistories.filter(
          (pricingHistory: PricingHistory) => {
            return (
              dayjs().valueOf() >
                dayjs(pricingHistory.asOfDateStd, 'YYYY-MM-DD').valueOf() &&
              this.state.historicalData.find(
                (distData) =>
                  distData.distributionAsOfDateStd ===
                  pricingHistory.asOfDateStd
              )
            );
          }
        );
      }
      this.state.tableConfig.dividendYears = sortedUniq(
        this.state.historicalData.map((distributionHistory) =>
          distributionHistory.distributionAsOfDateYear.toString()
        )
      );
      this.state.tableConfig.dividendYears.push('SINCE_INCEPTION'); // to be always set as last option.

      // make default selection to Since Inception.
      this.state.tableConfig.selectedYear = this.state.tableConfig.dividendYears[
        this.state.tableConfig.dividendYears.length - 1
      ];

      this.createDistributionTable(this.state.tableConfig.selectedYear);
    }

    this.state.hasLoadedFullFeed = true;
    this.distributionState.next(this.state);
  }

  public setConfigurationName(configurationName: ConfigurationId): void {
    this.configurationName = configurationName;
  }

  public getConfigurationName(): ConfigurationId {
    return this.configurationName;
  }

  /**
   * ******************** Distribution history table helper methods ********************
   */

  /**
   * Filter the historical distribution table data as per the year selected.
   * @param selectedYear distribution year selected
   */
  public createDistributionTable(selectedYear: string): void {
    let filteredRecords: DistributionHistory[] = [];
    let filteredPricingRecords: PricingHistory[] = [];

    if (selectedYear === 'SINCE_INCEPTION') {
      filteredRecords = cloneDeep(this.state.historicalData);
      filteredPricingRecords = cloneDeep(this.state.pricingHistoricalData);
    } else {
      filteredRecords = cloneDeep(
        this.state.historicalData.filter(
          (distributionHistory) =>
            distributionHistory.distributionAsOfDateYear === selectedYear
        )
      );
      filteredPricingRecords = cloneDeep(
        this.state.pricingHistoricalData.filter((pricingHistory) => {
          const asOfDateYear = pricingHistory?.asOfDateStd
            ? pricingHistory?.asOfDateStd?.split('-')[0]
            : null;
          return asOfDateYear === selectedYear;
        })
      );
    }
    this.pagedRecords.records = filteredRecords;
    this.pagedRecords.pageNumber = 1; // post new selection, set page number to 1.

    this.state.tableConfig.selectedYear = selectedYear;
    this.state.tableConfig.startDate = this.pagedRecords.records[
      this.pagedRecords.records?.length - 1
    ]?.incomeDistributionRecordDate;
    this.state.tableConfig.endDate = this.pagedRecords.records[0]?.incomeDistributionRecordDate;

    const highChartOptions = filteredPricingRecords?.length
      ? this.fetchDistributionNavChartOptions()
      : this.fetchChartOptions();
    highChartOptions.series = this.fetchDistributionSeries(
      filteredRecords,
      filteredPricingRecords
    );
    this.state.highChartOptions = highChartOptions;
    // expand tables for Smarsh capture
    this.debugService
      .getExpandAllComponents$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((expandAll: boolean) => {
        if (expandAll) {
          this.pageSize = this.pagedRecords.expandAll();
        }
      });

    // Below condition will not trigger state update on initial load.
    // Let it be done in above 'mapState' method.
    // Next time, when range selection happens, then trigger state change.

    if (this.state.hasLoadedFullFeed) {
      this.distributionState.next(this.state);
    }
  }

  /**
   * Calculate historical start date.
   */
  private getHistoricalStartDate(): string {
    // COREWEB-3143 : first year of data not alway inception year
    const isInceptionYear =
      this.state.latestConfig.shareClassInceptionDateStd.indexOf(
        this.state.tableConfig.selectedYear
      ) === 0;

    if (
      isInceptionYear ||
      this.state.tableConfig.selectedYear === 'SINCE_INCEPTION'
    ) {
      return this.state.latestConfig.shareClassInceptionDate;
    }

    return dayjs(
      `${this.state.tableConfig.selectedYear}-01-01`,
      'YYYY-MM-DD'
    ).format('L');
  }

  /**
   * Calculate historical end date.
   */
  private getHistoricalEndDate(): string {
    const lastYear = first(this.state.tableConfig.dividendYears);
    if (
      this.state.tableConfig.selectedYear === lastYear ||
      this.state.tableConfig.selectedYear === 'SINCE_INCEPTION'
    ) {
      if (!this.state.isSiteIntl) {
        return dayjs(`${lastYear}-12-31`, 'YYYY-MM-DD').format('L');
      }
      return dayjs().format('L');
    }

    return dayjs(
      `${this.state.tableConfig.selectedYear}-12-31`,
      'YYYY-MM-DD'
    ).format('L');
  }

  /**
   * Return the boolean value for preference on income over capgain values.
   * @param distributionHistory distribution history object
   */
  private showIncomeOverCapgains(
    distributionHistory: DistributionHistory
  ): boolean {
    return distributionHistory.incomeDistributionExDividendDate ? true : false;
  }

  /**
   * Handle download action.
   * Convert table records into excel format and initiate download.
   */
  public triggerDownloadAction(): void {
    const mappedHeader: Map<string, string> = this.mapDataHeader(
      this.state.isSiteIntl,
      this.state.latestConfig.isSgHk,
      this.state.latestConfig.showCanadaDistForEtfMfMmf,
      this.state.latestConfig.hideExDistDate
    );

    const mappedBody: DownloadableTableRowData[] = this.mapDownloadData(
      this.getTotalFilteredRecords(),
      this.state.isSiteIntl,
      this.state.latestConfig.isSgHk,
      this.state.latestConfig.showCanadaDistForEtfMfMmf,
      this.state.latestConfig.hideExDistDate
    );

    const mappedFullRecords: FtCellObject[][] = [
      Object.keys(mappedBody[0]).map((data) =>
        getCellObject(this.translateService.instant(mappedHeader.get(data)))
      ),
    ];

    mappedBody.forEach((data) => {
      mappedFullRecords.push(Object.values(data));
    });

    const fundName: FtCellObject[][] = [];
    const shareClassSection: FtCellObject[][] = [];
    const endDateStartDateSection: FtCellObject[][] = [];

    fundName.push([getCellObject(this.state.fundName, 's')]);

    if (!this.state.latestConfig.isSingleShareClass) {
      shareClassSection.push([
        getCellObject(
          `${this.translateService.instant('pds.SHARE_CLASS_NAME_STD')}`,
          's'
        ),
        getCellObject(this.state.latestConfig.shareClassName, 's'),
      ]);
    }

    shareClassSection.push([
      getCellObject(
        `${this.translateService.instant('products.inception-date')}`,
        's'
      ),
      getCellObject(this.state.latestConfig.shareClassInceptionDate, 's'),
    ]);
    shareClassSection.push([
      getCellObject(
        `${this.translateService.instant('pds.SHARE_CLASS_CURRENCY')}`,
        's'
      ),
      getCellObject(this.state.historicalData[0].currencyCode, 's'),
    ]);

    endDateStartDateSection.push([
      getCellObject(
        `${this.translateService.instant(
          'products.distribution-excel-download-dividends'
        )} : ${this.translateService.instant('products.calendar-from')} ${
          this.state.tableConfig.startDate
        } ${this.translateService.instant('products.calendar-to')} ${
          this.state.tableConfig.endDate
        }`,
        's'
      ),
    ]);
    forkJoin([
      this.excelDownloadService.getProximalCaveat$(
        'HistoricalDividendDataExcelBottom'
      ),
      this.excelDownloadService.getProximalCaveat$(
        'HistoricalDividendDataExcelTop'
      ),
    ]).subscribe((data) => {
      const [bottomCaveat, topCaveat]: [
        FtCellObject[][],
        FtCellObject[][]
      ] = data;
      this.exportToExcel(
        mappedFullRecords,
        topCaveat,
        bottomCaveat,
        fundName,
        shareClassSection,
        endDateStartDateSection
      );
    });
  }

  /**
   * Return mapped header for corresponding column data.
   * @param isSiteIntl boolean flag for site international or US
   */
  private mapDataHeader(
    isSiteIntl: boolean,
    isSgHk: boolean,
    showCanadaDistForEtfMfMmf: boolean,
    hideExDistDate: boolean
  ): Map<string, string> {
    const mappedHeader = new Map<string, string>();

    if (isSiteIntl) {
      mappedHeader.set('currency', 'pds.SHARE_CLASS_CURRENCY');
      mappedHeader.set('recordDate', 'pds.INCM_DIST_RECORD_DATE');
      if (!hideExDistDate) {
        mappedHeader.set('exDividendDate', 'pds.INCM_DIST_EX_DIV_DATE');
      }
      mappedHeader.set('distributionDate', 'pds.INCM_DIST_PAYABLE_DATE');
      mappedHeader.set('nav', 'pds.NAV_VALUE');
      mappedHeader.set('distributionPerUnit', 'pds.INCM_DIST_AMT');
      if (isSgHk) {
        mappedHeader.set('divDistributionIncome', 'pds.INCM_DIST_INCOME');
        mappedHeader.set('dividendCapital', 'pds.INCM_DIV_CAPITAL');
      } else if (showCanadaDistForEtfMfMmf) {
        if (this.state.productType === 'ETFs') {
          mappedHeader.set('exDividendDate', 'products.dist-ex-dist-date');
          mappedHeader.set(
            'incomeDistributionAmount',
            'products.dist-cash-dist-per-unit'
          );
          mappedHeader.set(
            'reInvestmentAmount',
            'products.dist-rein-dist-per-unit'
          );
          mappedHeader.set(
            'totalDistributionAmountMonthly',
            'products.dist-total-dist-per-unit'
          );
        } else if (
          this.state.productType === 'Mutual Funds' ||
          this.state.productType === 'Money Market Funds'
        ) {
          mappedHeader.set('currency', 'pds.SHARE_CLASS_CURRENCY');
          mappedHeader.set('recordDate', 'pds.INCM_DIST_RECORD_DATE');
          mappedHeader.set('exDividendDate', 'pds.INCM_DIST_PAYABLE_DATE');
          mappedHeader.set('exDividendNavValue', 'products.dist-dist-price');
          mappedHeader.set(
            'incomeDistributionAmount',
            'products.dist-inv-income'
          );
          mappedHeader.set(
            'returnOfCapitalAmount',
            'products.dist-return-of-cap'
          );
          mappedHeader.set('longTermCapitalGain', 'products.dist-capgains');
          mappedHeader.set(
            'totalDistributionAmountMonthly',
            'products.dist-total-dist-per-unit'
          );
        } else {
          this.mapXlsHeaderForIntl(mappedHeader);
        }
      }
    } else {
      this.mapXlsHeaderForIntl(mappedHeader);
    }

    return mappedHeader;
  }

  private mapXlsHeaderForIntl(mappedHdr: Map<string, string>): void {
    mappedHdr.set('incomeDistributionRecordDate', 'pds.INCM_DIST_RECORD_DATE');
    mappedHdr.set('capitalGainRecordDate', 'pds.INCM_DIST_RECORD_DATE');
    mappedHdr.set(
      'incomeDistributionExDividendDate',
      'pds.INCM_DIST_EX_DIV_DATE'
    );
    mappedHdr.set('capitalGainExDividendDate', 'pds.INCM_DIST_EX_DIV_DATE');
    mappedHdr.set('reInvestmentDate', 'pds.REINVESTMENT_DATE');
    mappedHdr.set('capitalGainReInvestmentDate', 'pds.REINVESTMENT_DATE');
    mappedHdr.set(
      'incomeDistributionExPayableDate',
      'pds.INCM_DIST_PAYABLE_DATE'
    );
    mappedHdr.set('capitalGainPayableDate', 'pds.INCM_DIST_PAYABLE_DATE');
    mappedHdr.set('reInvestmentAmount', 'pds.REINVESTMENT_AMT');
    if (this.state.productType !== ConfigurationId.TOF) {
      mappedHdr.set('longTermCapitalGain', 'pds.CAPGAIN_AMT_LONG_TERM');
      mappedHdr.set('shortTermCapitalGain', 'pds.CAPGAIN_AMT_SHRT_TERM');
    }
    mappedHdr.set('incomeDistributionAmount', 'pds.INCM_DIST_AMT');
    if (this.state.productType !== ConfigurationId.TOF) {
      mappedHdr.set('taxExemptIncomeAmount', 'pds.TAXABLE_EXEMPT_INCOME');
    }
    mappedHdr.set('returnOfCapitalAmount', 'pds.RETURN_OF_CAPITAL_AMT');
    mappedHdr.set('totalDistributionAmountMonthly', 'pds.DIVIDENDPERSHARE');
  }

  /**
   * Return mapped data as per downloadable action.
   * @param totalRecords total filtered records
   * @param isSiteIntl boolean flag for site international or US
   */
  private mapDownloadData(
    totalRecords: DistributionHistory[],
    isSiteIntl: boolean,
    isSgHk: boolean,
    showCanadaDistForEtfMfMmf: boolean,
    hideExDistDate: boolean
  ): DownloadableTableRowData[] {
    const mappedData: DownloadableTableRowData[] = [];

    if (isSiteIntl) {
      if (isSgHk) {
        totalRecords.forEach((record) => {
          mappedData.push({
            currency: record.currencyCode,
            recordDate: record.incomeDistributionRecordDate,
            exDividendDate: record.incomeDistributionExDividendDate,
            distributionDate: record.incomeDistributionExPayableDate,
            nav: record.exDividendNavValue,
            distributionPerUnit: record.incomeDistributionAmount,
            divDistributionIncome: record.divDistributionIncome,
            dividendCapital: record.dividendCapital,
          });
        });
      } else if (showCanadaDistForEtfMfMmf) {
        if (this.state.productType === 'ETFs') {
          totalRecords.forEach((record) => {
            mappedData.push({
              currency: record.currencyCode,
              recordDate: record.incomeDistributionRecordDate,
              exDividendDate: record.incomeDistributionExDividendDate,
              distributionDate: record.incomeDistributionExPayableDate,
              incomeDistributionAmount: record.incomeDistributionAmount,
              reInvestmentAmount: record.reInvestmentAmount,
              totalDistributionAmountMonthly:
                record.totalDistributionAmountMonthly,
            });
          });
        } else if (
          this.state.productType === 'Mutual Funds' ||
          this.state.productType === 'Money Market Funds'
        ) {
          totalRecords.forEach((record) => {
            mappedData.push({
              currency: record.currencyCode,
              recordDate: record.incomeDistributionRecordDate,
              exDividendDate: record.incomeDistributionExDividendDate,
              exDividendNavValue: record.exDividendNavValue,
              incomeDistributionAmount: record.incomeDistributionAmount,
              returnOfCapitalAmount: record.returnOfCapitalAmount,
              longTermCapitalGain: record.longTermCapitalGain,
              totalDistributionAmountMonthly:
                record.totalDistributionAmountMonthly,
            });
          });
        }
      } else if (hideExDistDate) {
        totalRecords.forEach((record) => {
          mappedData.push({
            currency: record.currencyCode,
            recordDate: record.incomeDistributionRecordDate,
            distributionDate: record.incomeDistributionExPayableDate,
            nav: record.exDividendNavValue,
            distributionPerUnit: record.incomeDistributionAmount,
          });
        });
      } else {
        totalRecords.forEach((record) => {
          mappedData.push({
            currency: record.currencyCode,
            recordDate: record.incomeDistributionRecordDate,
            exDividendDate: record.incomeDistributionExDividendDate,
            distributionDate: record.incomeDistributionExPayableDate,
            nav: record.exDividendNavValue,
            distributionPerUnit: record.incomeDistributionAmount,
          });
        });
      }
    } else {
      totalRecords.forEach((record) => {
        if (this.showIncomeOverCapgains(record)) {
          const ovrCapGains = {
            incomeDistributionRecordDate: record.incomeDistributionRecordDate,
            incomeDistributionExDividendDate:
              record.incomeDistributionExDividendDate,
            reInvestmentDate: record.reInvestmentDate,
            incomeDistributionExPayableDate:
              record.incomeDistributionExPayableDate,
            reInvestmentAmount: record.reInvestmentAmount,
            longTermCapitalGain: record.longTermCapitalGain,
            shortTermCapitalGain: record.shortTermCapitalGain,
            incomeDistributionAmount: record.incomeDistributionAmount,
            taxExemptIncomeAmount: record.taxExemptIncomeAmount,
            returnOfCapitalAmount: record.returnOfCapitalAmount,
            totalDistributionAmountMonthly:
              record.totalDistributionAmountMonthly,
          };
          if (this.state.productType === ConfigurationId.TOF) {
            delete ovrCapGains.longTermCapitalGain;
            delete ovrCapGains.shortTermCapitalGain;
            delete ovrCapGains.taxExemptIncomeAmount;
            mappedData.push(ovrCapGains);
          } else {
            mappedData.push(ovrCapGains);
          }
        } else {
          mappedData.push({
            capitalGainRecordDate: record.capitalGainRecordDate,
            capitalGainExDividendDate: record.capitalGainExDividendDate,
            capitalGainReInvestmentDate: record.capitalGainReInvestmentDate,
            capitalGainPayableDate: record.capitalGainPayableDate,
            reInvestmentAmount: record.reInvestmentAmount,
            longTermCapitalGain: record.longTermCapitalGain,
            shortTermCapitalGain: record.shortTermCapitalGain,
            incomeDistributionAmount: record.incomeDistributionAmount,
            taxExemptIncomeAmount: record.taxExemptIncomeAmount,
            returnOfCapitalAmount: record.returnOfCapitalAmount,
            totalDistributionAmountMonthly:
              record.totalDistributionAmountMonthly,
          });
        }
      });
    }

    return mappedData;
  }

  /**
   * Export the table data to excel and auto download.
   * @param data full record for excel
   */
  private exportToExcel(
    data: FtCellObject[][],
    topCaveat: FtCellObject[][],
    bottomCaveat: FtCellObject[][],
    fundName: FtCellObject[][],
    shareClassSection: FtCellObject[][],
    endDateStartDateSection: FtCellObject[][]
  ): void {
    import('xlsx').then((xlsx) => {
      const workSheet = xlsx.utils.aoa_to_sheet(topCaveat);
      const workBook = xlsx.utils.book_new();

      workSheet['!cols'] = this.fitToColumn(fundName);

      xlsx.utils.sheet_add_aoa(workSheet, fundName, {
        origin: 'A' + (topCaveat.length + 2),
      });

      xlsx.utils.sheet_add_aoa(workSheet, shareClassSection, {
        origin: 'A' + (topCaveat.length + fundName.length + 3),
      });

      xlsx.utils.sheet_add_aoa(workSheet, endDateStartDateSection, {
        origin:
          'A' +
          (topCaveat.length + fundName.length + shareClassSection.length + 4),
      });

      xlsx.utils.sheet_add_aoa(workSheet, data, {
        origin:
          'A' +
          (topCaveat.length +
            fundName.length +
            shareClassSection.length +
            endDateStartDateSection.length +
            6),
      });

      xlsx.utils.sheet_add_aoa(workSheet, bottomCaveat, {
        origin:
          'A' +
          (data.length +
            8 +
            topCaveat.length +
            fundName.length +
            endDateStartDateSection.length +
            shareClassSection.length),
      });

      xlsx.utils.book_append_sheet(
        workBook,
        workSheet,
        this.translateService.instant(
          'products.distribution-history-report-tabname'
        )
      );
      xlsx.writeFile(
        workBook,
        this.translateService.instant(
          'products.distribution-history-report-filename'
        )
      );
    });
  }

  /**
   * Get maximum character of each column.
   * @param data full record for excel
   */
  private fitToColumn(data: FtCellObject[][]) {
    return data[0].map((a, i) => ({
      wch: Math.max(
        ...data.map((a2: FtCellObject[]) => {
          return a2[i]?.v?.toString().length;
        })
      ),
    }));
  }

  /**
   * ******************** Pagination helper methods ********************
   */

  /**
   * Return total number of pages as observable.
   */
  public getNumberOfPages$(): Observable<number> {
    return this.pagedRecords.numberOfPages$;
  }

  /**
   * Return current page records as observable.
   */
  public getCurrentPage$(): Observable<DistributionHistory[]> {
    return this.pagedRecords.currentPage$;
  }

  /**
   * Trigger page number selection accordingly.
   * @param n page number selected
   */
  public goToPage(n: number): void {
    if (this.pagedRecords) {
      this.pagedRecords.pageNumber = n;
    }
  }

  /**
   * Return total filtered records.
   */
  private getTotalFilteredRecords(): DistributionHistory[] {
    return this.pagedRecords.records;
  }

  public showCanadaDistForEtfMfMmf(prodType: ProductType): boolean {
    return (
      this.siteConfigService.isCanada() &&
      (prodType === 'ETFs' ||
        prodType === 'Mutual Funds' ||
        prodType === 'Money Market Funds')
    );
  }

  public hideExDistDateForCountry(): boolean {
    const countriesWithHiddenExDistDate = ['AU'];
    return countriesWithHiddenExDistDate.includes(
      this.siteConfigService?.product?.site?.country
    );
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   *
   * @param distributionHistory distribution history
   * @returns SeriesOptionsType
   */
  private fetchDistributionSeries(
    distributionHistory: DistributionHistory[],
    filteredPricingHistory: PricingHistory[]
  ): SeriesOptionsType[] {
    const distributionValues = this.convertDistributionHistoryIntoSeries(
      distributionHistory
    );
    const label = this.getDistributionPerShareLabel();

    let distributionSeries: SeriesOptionsType = {
      name: label,
      type: 'line',
      tooltip: {
        pointFormat: `
          <span style='font-size:14px;color:{series.color}'>\u25CF</span>
          <span>{series.name}: </span>
          <b>${symbols.get(this.state.latestConfig.currencyCode)}{point.y}</b>
        `,
      },
      data: distributionValues,
    };

    if (filteredPricingHistory?.length) {
      const pricingValues = this.convertPricingHistoryIntoSeries(
        filteredPricingHistory
      );

      distributionSeries = {
        name: label,
        type: 'column',
        yAxis: 1,
        tooltip: {
          pointFormat: `
            <span style='font-size:14px;color:{series.color}'>\u25CF</span>
            <span>{series.name}: </span>
            <b>${symbols.get(this.state.latestConfig.currencyCode)}{point.y}</b>
          `,
        },
        data: distributionValues,
      };

      if (this.highchartsTheme?.dualAxisColor) {
        distributionSeries.color = this.highchartsTheme?.dualAxisColor;
      }

      const pricingSeries: SeriesOptionsType = {
        name: this.translateService.instant('products.NAV_VALUE'),
        type: 'spline',
        tooltip: {
          pointFormat: `
          <br><span style='font-size:14px;color:{series.color}'>\u25CF</span>
          <span>{series.name}: </span>
          <b>${symbols.get(this.state.latestConfig.currencyCode)}{point.y}</b>
        `,
        },
        data: pricingValues,
      };
      return [distributionSeries, pricingSeries];
    }

    return [distributionSeries];
  }
  /**
   * Get label for DistributionPerShare
   * @returns label
   */
  private getDistributionPerShareLabel(): string {
    if (this.state.isSiteIntl) {
      if (this.state.latestConfig.showCanadaDistForEtfMfMmf) {
        return this.translateService.instant(
          'products.dist-total-dist-per-unit'
        );
      } else {
        return this.translateService.instant('pds.DIVIDENDPERSHARE');
      }
    } else {
      return this.translateService.instant('pds.TOTAL_DISTRIBUTION_PER_SHARE');
    }
  }

  /**
   * Convert distribution history into series of record for chart
   * @param distributionHistory distribution history
   * @returns converted series of chart data
   */
  private convertDistributionHistoryIntoSeries(
    distributionHistory: DistributionHistory[]
  ) {
    const distributionValues = [];

    if (distributionHistory) {
      const distributionHistoryLength = distributionHistory.length - 1;
      for (let c = distributionHistoryLength; c >= 0; c--) {
        const distributionPoint = distributionHistory[c];
        let ts = this.formatStdDateToUnixTimeStamp(
          distributionPoint.distributionAsOfDateStd
        );
        let val: number = null;
        ts += 1 * 1000;
        val = parseNumber(distributionPoint.totalDistributionAmountMonthly);
        if ((val || val === 0) && !isNaN(val)) {
          distributionValues.push([ts, val]);
        }
      }
    }

    return distributionValues;
  }

  /**
   * Convert distribution history into series of record for chart
   * @param distributionHistory distribution history
   * @returns converted series of chart data
   */
  private convertPricingHistoryIntoSeries(pricingHistory: PricingHistory[]) {
    const navValues = [];

    if (pricingHistory) {
      const pricingHistoryLength = pricingHistory.length - 1;
      for (let c = pricingHistoryLength; c >= 0; c--) {
        const pricingPoint = pricingHistory[c];
        let ts = this.formatStdDateToUnixTimeStamp(pricingPoint.asOfDateStd);
        let val: number = null;
        ts += 1 * 1000;
        val = parseNumber(pricingPoint.navStd);
        if ((val || val === 0) && !isNaN(val)) {
          navValues.push([ts, val]);
        }
      }
    }

    return navValues;
  }

  /**
   * Return UTC standard unix timestamp, when given std format day eg YYYY-MM-DD
   * @param val date in format locale or YYYY-MM-DD
   * @returns UTC unix timestamp
   */
  private formatStdDateToUnixTimeStamp(val) {
    if (!val || val === '-') {
      return null;
    }

    const stdDateFormatMatch = val.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})/);
    if (!stdDateFormatMatch) {
      return null;
    }

    const year = stdDateFormatMatch[1];
    const month = stdDateFormatMatch[2] - 1;
    const day = stdDateFormatMatch[3];
    const date = Date.UTC(year, month, day, 0, 0);

    return date.valueOf();
  }

  /**
   * Fetch initial chart options.
   */
  private fetchChartOptions(): Highcharts.Options {
    return {
      title: {
        text: undefined,
      },
      chart: {
        spacingLeft: 5,
        spacingRight: 0,
        marginRight: 20,
        events: {
          render(this) {
            const that = this;
            setTimeout(() => {
              that.reflow();
            }, 0);
          },
        },
        reflow: true,
      },
      xAxis: {
        type: 'datetime',
        gridLineDashStyle: 'LongDash',
        // COREWEB-2921 increase tickPixelInterval from default 100, to force extra width for labels
        // NB: new value found by trial and error, and doesn't seem to reflect the actual pixel width
        tickPixelInterval: 110,
        // WORKAROUND: COREWEB-1543 set hour/minute/second to day display
        dateTimeLabelFormats: {
          millisecond: '%e. %b',
          second: '%e. %b',
          minute: '%e. %b',
          hour: '%e. %b',
        },
      },
      yAxis: [
        {
          gridLineDashStyle: 'LongDash',
          labels: {
            format:
              symbols.get(this.state.latestConfig.currencyCode) + '{value}',
          },
          title: {
            text: null,
          },
          minorTickInterval: 'auto',
        },
      ],
      legend: {
        enabled: true,
        verticalAlign: 'bottom',
      },
      tooltip: {
        shared: true,
        useHTML: true,
        // COREWEB-1277 set to day precision
        // http://api.highcharts.com/highcharts/tooltip.dateTimeLabelFormats
        xDateFormat: getHighchartsLocaleFormat(),
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false,
          },
        },
        line: {
          marker: {
            enabled: false,
          },
        },
      },
    };
  }

  /**
   * Fetch initial chart options.
   */
  private fetchDistributionNavChartOptions(): Highcharts.Options {
    return {
      title: {
        text: undefined,
      },
      chart: {
        spacingLeft: 0,
        spacingRight: 0,
        marginRight: 80,
        events: {
          render(this) {
            const that = this;
            setTimeout(() => {
              that.reflow();
            }, 0);
          },
        },
        reflow: true,
      },
      xAxis: {
        type: 'datetime',
        gridLineDashStyle: 'LongDash',
        ordinal: true,
        // COREWEB-2921 increase tickPixelInterval from default 100, to force extra width for labels
        // NB: new value found by trial and error, and doesn't seem to reflect the actual pixel width
        tickPixelInterval: 110,
        // WORKAROUND: COREWEB-1543 set hour/minute/second to day display
        dateTimeLabelFormats: {
          millisecond: '%e. %b',
          second: '%e. %b',
          minute: '%e. %b',
          hour: '%e. %b',
        },
      },
      yAxis: [
        {
          // Primary yAxis
          labels: {
            format:
              symbols.get(this.state.latestConfig.currencyCode) + '{value}',
          },
          title: {
            text: this.translateService.instant('products.NAV_VALUE'),
          },
        },
        {
          gridLineDashStyle: 'LongDash',
          labels: {
            format:
              symbols.get(this.state.latestConfig.currencyCode) + '{value}',
          },
          title: {
            text: this.getDistributionPerShareLabel(),
          },
          minorTickInterval: 'auto',
          opposite: true,
        },
      ],

      legend: {
        enabled: true,
        verticalAlign: 'bottom',
      },
      tooltip: {
        shared: true,
        useHTML: true,
        // COREWEB-1277 set to day precision
        // http://api.highcharts.com/highcharts/tooltip.dateTimeLabelFormats
        xDateFormat: '%B %Y',
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false,
          },
        },
        line: {
          marker: {
            enabled: false,
          },
        },
      },
    };
  }

  /**
   * Update toggle
   * @param selectedToggle selected toggle
   */
  public updateToggle(selectedToggle: string) {
    this.state.selectedTab = selectedToggle;
    if (this.state.hasLoadedFullFeed) {
      this.distributionState.next(this.state);
    }
  }
}
